import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import Modal from "@cloudscape-design/components/modal";
import Box from "@cloudscape-design/components/box";
import SpaceBetween from "@cloudscape-design/components/space-between";
import Button from "@cloudscape-design/components/button";
import FormField from "@cloudscape-design/components/form-field";
import Input from "@cloudscape-design/components/input";
import Alert from "@cloudscape-design/components/alert";
import Checkbox from "@cloudscape-design/components/checkbox";
import Badge from "@cloudscape-design/components/badge";
import RadioGroup from "@cloudscape-design/components/radio-group";
import { prepareInboundCall, initiateOutboundCall, validateE164 } from "../services/callApi";

function generateSessionId() {
  return crypto.randomUUID();
}

function CallModal({ scenario, callDirection, onStart, onCancel, visible }) {
  const { t } = useTranslation();
  
  const [phoneNumber, setPhoneNumber] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [agentPhoneNumber, setAgentPhoneNumber] = useState("");
  const [agentPhoneError, setAgentPhoneError] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [inboundPhoneNumber, setInboundPhoneNumber] = useState("");
  const [inboundPhoneError, setInboundPhoneError] = useState("");
  const [rememberPhone, setRememberPhone] = useState(false);
  const [noiseProfile, setNoiseProfile] = useState("quiet");
  const sessionIdRef = useRef(null);

  useEffect(() => {
    if (visible && scenario) {
      setPhoneNumber("");
      setPhoneError("");
      setAgentPhoneNumber("");
      setAgentPhoneError("");
      setError("");
      sessionIdRef.current = generateSessionId();
      setNoiseProfile("quiet");

      // Read saved inbound phone number from localStorage
      try {
        const savedPhone = localStorage.getItem("inbound_phone_number");
        if (savedPhone) {
          setInboundPhoneNumber(savedPhone);
          setRememberPhone(true);
        } else {
          setInboundPhoneNumber("");
          setRememberPhone(false);
        }
      } catch {
        setInboundPhoneNumber("");
        setRememberPhone(false);
      }
      setInboundPhoneError("");
    }
  }, [visible, scenario, callDirection]);

  const handleInitiateOutbound = async () => {
    if (!phoneNumber.trim()) {
      setPhoneError(t("scenarios.call.errors.phone_required"));
      return;
    }
    if (!validateE164(phoneNumber)) {
      setPhoneError(t("scenarios.call.errors.invalid_phone"));
      return;
    }
    // Validate agent phone number if provided
    if (agentPhoneNumber.trim() && !validateE164(agentPhoneNumber)) {
      setAgentPhoneError(t("scenarios.call.errors.invalid_phone"));
      return;
    }
    
    setLoading(true);
    setError("");
    
    try {
      const agentNumber = agentPhoneNumber.trim() || null;
      const response = await initiateOutboundCall(scenario.scenario_id, phoneNumber.trim(), sessionIdRef.current, agentNumber, noiseProfile);
      onStart?.({
        ...response,
        session_id: sessionIdRef.current,
        agent_phone_number: agentNumber,
        noise_profile: noiseProfile
      });
    } catch (err) {
      if (err.isValidationError) {
        setPhoneError(err.message);
      } else {
        setError(err.message || t("scenarios.call.errors.initiate_failed"));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleStartMonitoring = async () => {
    // Validate inbound phone number
    if (!inboundPhoneNumber.trim()) {
      setInboundPhoneError(t("scenarios.call.errors.phone_required"));
      return;
    }
    if (!validateE164(inboundPhoneNumber)) {
      setInboundPhoneError(t("scenarios.call.errors.invalid_phone"));
      return;
    }
    // Validate agent phone number if provided
    if (agentPhoneNumber.trim() && !validateE164(agentPhoneNumber)) {
      setAgentPhoneError(t("scenarios.call.errors.invalid_phone"));
      return;
    }

    // Persist or remove phone number from localStorage
    try {
      if (rememberPhone) {
        localStorage.setItem("inbound_phone_number", inboundPhoneNumber.trim());
      } else {
        localStorage.removeItem("inbound_phone_number");
      }
    } catch {
      // Graceful degradation - continue even if localStorage fails
    }
    
    setLoading(true);
    setError("");
    
    try {
      const agentNumber = agentPhoneNumber.trim() || null;
      const response = await prepareInboundCall(scenario.scenario_id, inboundPhoneNumber.trim(), sessionIdRef.current, agentNumber, noiseProfile);
      onStart?.({ 
        scenario_id: scenario.scenario_id, 
        twilio_phone_number: response.twilio_phone_number, 
        session_id: sessionIdRef.current,
        agent_phone_number: agentNumber,
        noise_profile: noiseProfile
      });
    } catch (err) {
      setError(err.message || t("scenarios.call.errors.prepare_failed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      visible={visible}
      onDismiss={onCancel}
      header={
        <SpaceBetween direction="horizontal" size="xs" alignItems="center">
          <span>{callDirection === "inbound" ? t("scenarios.call.inbound_title") : t("scenarios.call.outbound_title")}</span>
          <Badge color={callDirection === "inbound" ? "green" : "blue"}>{t(`scenarios.call_direction.${callDirection}`)}</Badge>
        </SpaceBetween>
      }
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={onCancel} disabled={loading}>{t("scenarios.call.cancel")}</Button>
            {callDirection === "inbound" ? (
              <Button variant="primary" onClick={handleStartMonitoring} disabled={loading || !validateE164(inboundPhoneNumber)} loading={loading}>
                {t("scenarios.call.start_monitoring")}
              </Button>
            ) : (
              <Button variant="primary" onClick={handleInitiateOutbound} disabled={loading || !validateE164(phoneNumber)} loading={loading}>
                {t("scenarios.call.initiate_call")}
              </Button>
            )}
          </SpaceBetween>
        </Box>
      }
      size="medium"
    >
      <SpaceBetween size="m">
        {scenario && (
          <Box>
            <Box variant="awsui-key-label">{t("scenarios.call.scenario")}</Box>
            <Box variant="p" fontWeight="bold">{scenario.name}</Box>
            <Box variant="small" color="text-body-secondary">{scenario.description}</Box>
          </Box>
        )}
        
        {error && <Alert type="error">{error}</Alert>}
        
        {callDirection === "inbound" ? (
          <SpaceBetween size="m">
            <Box variant="p">{t("scenarios.call.inbound_instructions")}</Box>
            
            <FormField 
              label={t("scenarios.call.inbound_phone_label")} 
              description={t("scenarios.call.inbound_phone_description")} 
              errorText={inboundPhoneError}
              constraintText={t("scenarios.call.inbound_phone_constraint")}
            >
              <Input 
                value={inboundPhoneNumber} 
                onChange={({ detail }) => { setInboundPhoneNumber(detail.value); setInboundPhoneError(""); }} 
                placeholder={t("scenarios.call.phone_placeholder")} 
                disabled={loading} 
                type="tel" 
              />
            </FormField>

            <Checkbox
              checked={rememberPhone}
              onChange={({ detail }) => setRememberPhone(detail.checked)}
            >
              {t("scenarios.call.remember_phone")}
            </Checkbox>

            <FormField 
              label={t("scenarios.call.agent_phone_number")} 
              description={t("scenarios.call.agent_phone_description")} 
              errorText={agentPhoneError}
              constraintText={t("scenarios.call.phone_constraint")}
            >
              <Input 
                value={agentPhoneNumber} 
                onChange={({ detail }) => { setAgentPhoneNumber(detail.value); setAgentPhoneError(""); }} 
                placeholder={t("scenarios.call.phone_placeholder")} 
                disabled={loading} 
                type="tel" 
              />
            </FormField>
          </SpaceBetween>
        ) : (
          <SpaceBetween size="m">
            <Box variant="p">{t("scenarios.call.outbound_instructions")}</Box>
            <FormField label={t("scenarios.call.phone_number")} description={t("scenarios.call.phone_description")} errorText={phoneError} constraintText={t("scenarios.call.phone_constraint")}>
              <Input value={phoneNumber} onChange={({ detail }) => { setPhoneNumber(detail.value); setPhoneError(""); }} placeholder={t("scenarios.call.phone_placeholder")} disabled={loading} type="tel" />
            </FormField>
            
            <FormField 
              label={t("scenarios.call.agent_phone_number")} 
              description={t("scenarios.call.agent_phone_description")} 
              errorText={agentPhoneError}
              constraintText={t("scenarios.call.phone_constraint")}
            >
              <Input 
                value={agentPhoneNumber} 
                onChange={({ detail }) => { setAgentPhoneNumber(detail.value); setAgentPhoneError(""); }} 
                placeholder={t("scenarios.call.phone_placeholder")} 
                disabled={loading} 
                type="tel" 
              />
            </FormField>
          </SpaceBetween>
        )}

        <FormField 
          label={t("scenarios.call.noise_profile_label")} 
          description={t("scenarios.call.noise_profile_description")}
        >
          <RadioGroup
            value={noiseProfile}
            onChange={({ detail }) => setNoiseProfile(detail.value)}
            items={[
              { value: "quiet", label: t("scenarios.call.noise_quiet"), description: t("scenarios.call.noise_quiet_description") },
              { value: "moderate", label: t("scenarios.call.noise_moderate"), description: t("scenarios.call.noise_moderate_description") },
              { value: "noisy", label: t("scenarios.call.noise_noisy"), description: t("scenarios.call.noise_noisy_description") }
            ]}
          />
        </FormField>
      </SpaceBetween>
    </Modal>
  );
}

export default CallModal;
